import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Download, KeyRound, Plus, Trash2 } from 'lucide-react';

import type { ApiToken, CreateApiTokenBody } from '@tasks/contracts';

import { createApiToken, listApiTokens, revokeApiToken } from '../../api/integrations';
import { queryKeys } from '../../api/queryKeys';
import { useAuth } from '../../auth/AuthProvider';
import { useProjectMembers } from '../../hooks/useProject';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { EmptyState } from '../../components/EmptyState';
import { IconButton } from '../../components/IconButton';
import { Modal } from '../../components/Modal';
import { Skeleton } from '../../components/Skeleton';
import { Table, type TableColumn } from '../../components/Table';
import { useToast } from '../../components/Toast';
import { humanizeError } from '../../lib/errors';
import { formatDateTime } from '../../lib/format';
import { cn } from '../../lib/cn';
import { ApiTokenFormModal, SCOPE_LABELS } from './ApiTokenFormModal';
import styles from './SettingsPages.module.css';

/**
 * Готовое руководство по публичному API для внешних разработчиков.
 * Файл лежит в `packages/web/public/docs/` и раздаётся статикой как есть,
 * поэтому скачивание — обычная ссылка, без запроса к API.
 */
const API_GUIDE_URL = '/docs/api-v1.md';
const API_GUIDE_FILENAME = 'tasks-api-v1.md';

export interface ApiTokensSectionProps {
  readonly projectKey: string;
}

/**
 * Токены публичного API (docs/12 §Приложение A.1, UC-INT-01).
 *
 * Токен привязан к проекту и подставляется в `Authorization: Bearer`
 * запросов к `/api/v1`. Секрет показывается один раз: на сервере хранится
 * только его хеш, поэтому восстановить значение нельзя.
 */
export function ApiTokensSection({ projectKey }: ApiTokensSectionProps) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { user } = useAuth();
  const members = useProjectMembers(projectKey);

  const [isCreateOpen, setCreateOpen] = useState(false);
  const [issuedSecret, setIssuedSecret] = useState<string | null>(null);
  const [tokenToRevoke, setTokenToRevoke] = useState<ApiToken | null>(null);

  const tokensQuery = useQuery({
    queryKey: queryKeys.projects.apiTokens(projectKey),
    queryFn: ({ signal }) => listApiTokens(projectKey, signal),
    enabled: projectKey.length > 0,
  });

  const reportError = (cause: unknown): void => {
    const human = humanizeError(cause);
    toast.error(human.message, { code: human.code });
  };

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.projects.apiTokens(projectKey) });
  };

  const createMutation = useMutation({
    mutationFn: (values: CreateApiTokenBody) => createApiToken(projectKey, values),
    onSuccess: (response) => {
      setCreateOpen(false);
      setIssuedSecret(response.secret);
      invalidate();
    },
    onError: reportError,
  });

  const revokeMutation = useMutation({
    mutationFn: (tokenId: string) => revokeApiToken(projectKey, tokenId),
    onSuccess: () => {
      setTokenToRevoke(null);
      invalidate();
      toast.success('Токен отозван');
    },
    onError: reportError,
  });

  const tokens = tokensQuery.data ?? [];

  const copySecret = (value: string): void => {
    void navigator.clipboard.writeText(value).then(
      () => toast.success('Скопировано в буфер обмена'),
      () => toast.error('Не удалось скопировать'),
    );
  };

  const columns: readonly TableColumn<ApiToken>[] = [
    {
      id: 'name',
      header: 'Название',
      render: (token) => (
        <>
          <span className={styles['boardName']}>{token.name}</span>
          {token.owner ? <span className="u-muted"> · @{token.owner.login}</span> : null}
          {token.expiresAt ? <div className="u-muted">истекает {formatDateTime(token.expiresAt)}</div> : null}
        </>
      ),
    },
    { id: 'prefix', header: 'Префикс', render: (token) => <span className="u-mono">{token.prefix}</span> },
    {
      id: 'scopes',
      header: 'Права',
      render: (token) => (
        <span className={styles['scopeChips']}>
          {token.scopes.map((scope) => (
            <Badge key={scope} tone="neutral">
              {SCOPE_LABELS.find((item) => item.value === scope)?.label ?? scope}
            </Badge>
          ))}
        </span>
      ),
    },
    {
      id: 'lastUsed',
      header: 'Последнее использование',
      render: (token) => (
        <span className="u-muted">
          {token.lastUsedAt === null ? 'Никогда' : formatDateTime(token.lastUsedAt)}
        </span>
      ),
    },
    { id: 'requests', header: 'Запросов', render: (token) => <span className="u-mono">{token.requestCount}</span> },
    {
      id: 'actions',
      header: '',
      width: '48px',
      align: 'end',
      render: (token) => (
        <IconButton
          size="sm"
          tone="danger"
          label={`Отозвать токен ${token.name}`}
          icon={<Trash2 size={14} />}
          onClick={() => setTokenToRevoke(token)}
        />
      ),
    },
  ];

  if (tokensQuery.isPending) {
    return <Skeleton variant="row" count={4} />;
  }

  return (
    <section className={styles['section']}>
      <div className={styles['sectionHeader']}>
        <h2>Токены API ({tokens.length})</h2>
        <div className={styles['actions']}>
          {/*
            Скачивание — обычная ссылка на статический файл. `download` задаёт
            имя сохраняемого файла; адрес ведёт на руководство, которое лежит
            в сборке как есть (docs/12 §Приложение A).
          */}
          <a
            className={styles['downloadLink']}
            href={API_GUIDE_URL}
            download={API_GUIDE_FILENAME}
            data-testid="download-api-guide"
          >
            <Download aria-hidden size={14} />
            Скачать руководство
          </a>
          <Button variant="primary" icon={<Plus size={14} />} onClick={() => setCreateOpen(true)}>
            Выпустить токен
          </Button>
        </div>
      </div>

      <p className={styles['hint']}>
        Токен даёт доступ только к этому проекту: он подставляется в заголовок
        <code> Authorization: Bearer </code>
        запросов к <code>/api/v1</code>. Секрет показывается один раз при выпуске.
      </p>

      <p className={styles['hint']}>
        Руководство <code>{API_GUIDE_FILENAME}</code> описывает все методы <code>/api/v1</code>,
        права токена, лимиты и подпись вебхуков. Его можно отправить внешним разработчикам:
        внутренних методов в файле нет.
      </p>

      {tokens.length === 0 ? (
        <EmptyState
          icon={<KeyRound size={20} />}
          title="Токенов пока нет"
          description="Выпустите токен, чтобы внешняя система могла читать и изменять задачи проекта."
        />
      ) : (
        <Table
          ariaLabel="Токены API"
          columns={columns}
          rows={tokens}
          getRowId={(token) => token.id}
        />
      )}

      <ApiTokenFormModal
        open={isCreateOpen}
        isPending={createMutation.isPending}
        members={members}
        defaultOwnerId={
          members.find((member) => member.user.id === user?.id)?.user.id ??
          members[0]?.user.id ??
          user?.id ??
          ''
        }
        onSubmit={(values) => createMutation.mutate(values)}
        onClose={() => setCreateOpen(false)}
      />

      <Modal
        open={issuedSecret !== null}
        onClose={() => setIssuedSecret(null)}
        title="Токен выпущен"
        size="sm"
        footer={
          <Button variant="primary" onClick={() => setIssuedSecret(null)}>
            Готово
          </Button>
        }
      >
        <div className={styles['form']}>
          <p className={styles['warning']}>
            Скопируйте секрет сейчас: он показывается один раз и хранится на сервере только в виде
            хеша.
          </p>
          <div className={styles['secretRow']}>
            <code className={cn('u-mono', styles['secretValue'])}>{issuedSecret}</code>
            <IconButton
              size="sm"
              label="Скопировать секрет"
              icon={<Copy size={14} />}
              onClick={() => copySecret(issuedSecret ?? '')}
            />
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={tokenToRevoke !== null}
        title="Отозвать токен?"
        description={`Интеграции с токеном «${tokenToRevoke?.name ?? ''}» сразу потеряют доступ к проекту. Действие необратимо.`}
        confirmLabel="Отозвать"
        isPending={revokeMutation.isPending}
        onConfirm={() => tokenToRevoke && revokeMutation.mutate(tokenToRevoke.id)}
        onClose={() => setTokenToRevoke(null)}
      />
    </section>
  );
}
